import { useEffect, useRef, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import type { Glifo } from "./glifos.js";
import { CabecalhoDePagina } from "./CabecalhoDePagina.js";
import { useTrilhaAtiva } from "../estado/trilhas.js";

/**
 * Abas — o padrão de "vários recortes do mesmo assunto numa tela só".
 *
 * **Ele já existia três vezes.** `Revisar` tinha um `role="tab"` correto e
 * privado; `Estatisticas` e `Simulado` usavam a mesma classe `.abas` com
 * `role="group"`, que é a aparência da aba sem a semântica dela — o leitor de
 * tela anunciava um grupo de botões, e não "aba 2 de 3, selecionada". Extrair o
 * de `Revisar` conserta os outros dois de graça.
 *
 * **A aba escolhida mora na URL, em `?aba=`.** É o que faz o botão de voltar
 * funcionar dentro de uma tela de abas, um link levar direto ao recorte certo, e
 * um recarregamento não jogar a pessoa de volta na primeira. Estado local aqui
 * seria mais curto e perderia as três coisas — e a fusão de telas da RN-30
 * depende delas: `/importar` vira `/acervo?aba=importar`, e um favorito antigo
 * precisa continuar chegando onde chegava.
 *
 * A navegação por setas do padrão ARIA completo **não** está aqui, de propósito:
 * com `role="tab"` em `<button>` dentro de `role="tablist"`, o Tab já alcança
 * cada aba e o Enter já escolhe. Implementar foco gerenciado à mão é o tipo de
 * comportamento acessível difícil de acertar que a seção 10 manda não
 * reimplementar sem necessidade.
 */

export interface Aba {
  /** Vai para a URL: use algo estável e legível, não um índice. */
  id: string;
  rotulo: string;
  /** Opcional — o segundo sinal, quando a palavra sozinha não distingue. */
  glifo?: Glifo;
  /** Opcional: contagem à direita do rótulo. */
  contagem?: number;
  /**
   * O que esta aba é, em uma frase. Vira o subtítulo do cabeçalho da tela
   * enquanto ela está aberta.
   *
   * Ela existe porque as telas que viraram aba tinham subtítulo próprio, e ele
   * descrevia a aba e não o contêiner: "Cada trilha guarda seus próprios itens"
   * é sobre Trilhas, não sobre Acervo. Jogá-lo fora perderia a frase; deixá-lo
   * dentro do painel devolveria o segundo cabeçalho que esta fase tirou.
   */
  descricao?: string;
}

/**
 * A aba escolhida agora, e como trocá-la.
 *
 * Devolve sempre uma aba válida: `?aba=` com valor desconhecido — link velho,
 * URL digitada à mão — cai na primeira, em vez de deixar a tela sem painel.
 */
export function useAbaAtual(abas: readonly Aba[], parametro = "aba"): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const pedida = params.get(parametro);
  const atual = abas.some((a) => a.id === pedida) ? pedida! : (abas[0]?.id ?? "");

  const escolher = (id: string) => {
    const proximos = new URLSearchParams(params);
    proximos.set(parametro, id);
    // `replace`: trocar de aba não é um passo de navegação que mereça uma
    // entrada no histórico. Sem isto, sair da tela exigiria um "voltar" por
    // aba visitada.
    setParams(proximos, { replace: true });
  };

  return [atual, escolher];
}

/**
 * A fita de abas — **navegação**, e por isso desenhada como aba e não como
 * pílula.
 *
 * A distinção é a que os sistemas de design fazem e que este app tinha perdido:
 * **aba navega entre conteúdos distintos; controle segmentado troca a
 * apresentação do mesmo conteúdo.** As duas usavam a mesma pílula aqui, e em
 * `/estatisticas` elas apareciam **empilhadas** — `Evolução | Diagnóstico`, que
 * troca a tela, logo acima de `7 dias | 1 mês | 3 meses`, que troca a janela de
 * tempo do gráfico. Mesma forma, mesmo anel de ativo, dois significados.
 *
 * Ficou: **sublinhado indica aba**, e a pílula (`.abas` / `.aba`) fica sendo, e
 * só sendo, o filtro dentro da tela. Duas formas, dois papéis, e o olho separa
 * "isto troca a página" de "isto troca o dado" sem ler.
 */
export function Abas({
  abas,
  atual,
  aoEscolher,
  rotulo,
}: {
  abas: readonly Aba[];
  atual: string;
  aoEscolher: (id: string) => void;
  /** O que a lista de abas é, para o leitor de tela: "Listas de revisão". */
  rotulo: string;
}) {
  const fita = useRef<HTMLDivElement>(null);

  /*
   * A aba escolhida entra no campo de visão quando a fita não cabe.
   *
   * A fita rola na horizontal por necessidade — "Inteligência artificial" ao
   * lado de "Preferências" e "Conta" não cabe em 390 —, e sem isto a aba aberta
   * podia ficar cortada na borda, ou fora da tela, logo depois de ser escolhida
   * ou ao chegar por um link com `?aba=`.
   *
   * A rolagem é escrita à mão, e não por `scrollIntoView`: aquele rola **todo**
   * ancestral rolável, e num painel que já está no meio da página ele arrasta a
   * página junto. Aqui só `scrollLeft` da fita se mexe.
   */
  useEffect(() => {
    const caixa = fita.current;
    const alvo = caixa?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!caixa || !alvo) return;
    const inicio = alvo.offsetLeft;
    const fim = inicio + alvo.offsetWidth;
    if (inicio < caixa.scrollLeft) caixa.scrollLeft = inicio;
    else if (fim > caixa.scrollLeft + caixa.clientWidth) caixa.scrollLeft = fim - caixa.clientWidth;
  }, [atual, abas]);

  return (
    <div className="abas-nav" role="tablist" aria-label={rotulo} ref={fita}>
      {abas.map((a) => {
        const ativa = a.id === atual;
        return (
          <button
            key={a.id}
            type="button"
            role="tab"
            id={`aba-${a.id}`}
            aria-selected={ativa}
            aria-controls={`painel-${a.id}`}
            className={ativa ? "aba-nav aba-nav--ativa" : "aba-nav"}
            onClick={() => aoEscolher(a.id)}
          >
            {a.rotulo}
            {a.contagem !== undefined && <span className="dado aba-nav__contagem">{a.contagem}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * O cabeçalho de uma tela de abas — o *detail page header* do Carbon, na forma
 * que este app precisava.
 *
 * **Ele existe porque o contêiner não se nomeava.** Tocava-se **Acervo** na
 * navegação e a página se chamava **Trilhas**; tocava-se **Números** e ela se
 * chamava **Estatísticas**. Cada painel montava uma `.tela` com `<h1>` próprio,
 * e a fita de abas flutuava acima de um título que dizia outra coisa — em
 * Trilhas havia ainda um "‹ Início" entre as duas, de quando ela era destino
 * próprio, que fazia as abas parecerem pertencer à home.
 *
 * Agora o título é do contêiner, a aba aberta dá o subtítulo, e as abas ficam
 * presas embaixo dos dois. O `<h1>` da tela passa a ser um só, e ele é o nome
 * que está aceso na navegação.
 */
export function CabecalhoDaTela({
  titulo,
  abas,
  atual,
  aoEscolher,
  rotulo,
  acoes,
  estreito = false,
}: {
  titulo: string;
  abas: readonly Aba[];
  atual: string;
  aoEscolher: (id: string) => void;
  rotulo: string;
  acoes?: ReactNode;
  /** Tela de formulário (720px): o cabeçalho acompanha a coluna do conteúdo. */
  estreito?: boolean;
}) {
  const descricao = abas.find((a) => a.id === atual)?.descricao;
  const trilha = useTrilhaAtiva();
  return (
    <div className={estreito ? "cabecalho-tela cabecalho-tela--estreito" : "cabecalho-tela"}>
      <CabecalhoDePagina
        eyebrow={trilha ? `${titulo} · ${trilha.nome}` : titulo}
        titulo={titulo}
        subtitulo={descricao}
        acoes={acoes}
      >
        <Abas rotulo={rotulo} abas={abas} atual={atual} aoEscolher={aoEscolher} />
      </CabecalhoDePagina>
    </div>
  );
}

/** O painel de uma aba. Existe para que o par `id`/`aria-labelledby` não seja copiado à mão. */
export function PainelDaAba({
  id,
  className = "pilha",
  children,
}: {
  id: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div role="tabpanel" id={`painel-${id}`} aria-labelledby={`aba-${id}`} className={className}>
      {children}
    </div>
  );
}
